import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import {
  BrandLogo,
  Button,
  buttonClasses,
  cn,
  formatUsPhone,
  useSupportLineNumber,
} from "@dearbill/shared";
import { ChatWidget } from "./ChatWidget";
import { HeartHandshakeIcon, HomeIcon, PillIcon, ReceiptIcon, UserCogIcon } from "./icons";
import { MemberContactButton } from "./MemberContactButton";
import { PortalFooter } from "./PortalFooter";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

const NAV_ROW = "flex h-12 items-center gap-4 rounded-lg border-l-4 px-4";
const NAV_ROW_ACTIVE = "border-white bg-white/10 font-semibold text-white";
const NAV_ROW_IDLE = "border-transparent font-medium text-white/75";
/** The top bar's outlined control, the Contact us recipe; the Settings link fills it Ice while current. */
const TOP_BAR_LINK =
  "border-haze text-midnight hover:bg-ice border-[1.5px] bg-transparent px-[21px] py-2.5 text-[15px] font-semibold hover:opacity-100";

interface NavEntry {
  label: string;
  to: string;
  Icon: typeof HomeIcon;
}

/** Sidebar entries in the frame's order, each a `NavLink`, so the current one carries `aria-current="page"`. */
const NAV: readonly NavEntry[] = [
  { label: "Home", to: "/", Icon: HomeIcon },
  { label: "My Programs", to: "/programs", Icon: HeartHandshakeIcon },
  { label: "My Medications", to: "/medications", Icon: PillIcon },
  { label: "Cost Estimate", to: "/cost-estimate", Icon: ReceiptIcon },
  { label: "Account", to: "/account", Icon: UserCogIcon },
];

interface MemberLayoutProps {
  /** Portal session bearer, for the Contact us prefill. */
  session: string;
  onSignOut: () => void;
  children: ReactNode;
}

/**
 * The signed-in member portal chrome: a sidebar (logo, the Home, My Programs,
 * My Medications, Cost Estimate and Account links and the support line)
 * beside a column that holds the top bar (Contact us, the Settings link and
 * Sign out), the content and the footer. Below `lg` a logo bar above the top
 * bar replaces the sidebar. The Settings link is a `NavLink`, so it carries
 * `aria-current="page"` on `/settings`, where no sidebar row is current; it is
 * the page's only entry at every width.
 *
 * Landmarks: one `banner` (the header holds both bars), the `main` content,
 * and the footer outside `<main>` so it stays a `contentinfo` landmark. The
 * help block renders only when a support line is configured.
 */
export function MemberLayout({ session, onSignOut, children }: MemberLayoutProps): ReactNode {
  const supportLine = useSupportLineNumber(API_BASE);
  return (
    <div className="flex min-h-screen bg-white">
      <aside className="bg-deep-navy w-70 hidden shrink-0 flex-col gap-10 px-4 py-8 lg:flex">
        <div className="flex flex-col gap-2">
          <BrandLogo className="h-7.5 self-start brightness-0 invert" />
          <p className="text-body-sm font-medium uppercase text-white/75">
            Affordability Navigator
          </p>
        </div>
        <nav aria-label="Main">
          <ul className="text-body-sm flex flex-col gap-3">
            {NAV.map(({ label, to, Icon }) => (
              <li key={label}>
                <NavLink
                  to={to}
                  end
                  className={({ isActive }) =>
                    cn(NAV_ROW, isActive ? NAV_ROW_ACTIVE : NAV_ROW_IDLE)
                  }
                >
                  <Icon size={20} className="shrink-0" />
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        {supportLine !== null && (
          <div className="text-body-sm flex flex-col gap-2 pl-4">
            <p className="text-white/75">Need help?</p>
            <a href={`tel:${supportLine}`} className="font-semibold text-white hover:underline">
              {formatUsPhone(supportLine)}
            </a>
          </div>
        )}
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header>
          <div className="bg-deep-navy px-6 py-4 lg:hidden">
            <BrandLogo className="h-6 brightness-0 invert" />
          </div>
          <div className="border-haze flex items-center justify-end gap-6 border-b bg-white px-6 py-3 lg:h-20 lg:px-8 lg:py-0">
            <MemberContactButton session={session} />
            <NavLink
              to="/settings"
              end
              className={({ isActive }) =>
                buttonClasses("ghost", "sm", cn(TOP_BAR_LINK, isActive && "border-ice bg-ice"))
              }
            >
              Settings
            </NavLink>
            <Button variant="ghost" size="md" onClick={onSignOut} className="text-midnight">
              Sign out
            </Button>
          </div>
        </header>
        <main className="flex-1 p-6 lg:p-10">
          <div className="max-w-270 mx-auto flex w-full flex-col gap-8">{children}</div>
        </main>
        <PortalFooter />
      </div>
      <ChatWidget session={session} />
    </div>
  );
}
